import React, { useEffect, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Modal, TextField, CircularProgress, Snackbar, Alert, TablePagination, Checkbox } from '@mui/material';
import axios from 'axios';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';

interface ResellerMachine {
    id: number;
    workerId?: string;
    machineReference?: string;
    machineModel?: { modelName?: string };
    customer?: { customerGroup?: { name?: string } };
    hashVendor?: { name?: string };
    targetPool?: string;
    machineStatus?: string;
    devFee?: number;
    devFeeWorkerId?: string;
}

export default function ResellerDashboard() {
    const [machines, setMachines] = useState<ResellerMachine[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [totalElements, setTotalElements] = useState(0);
    
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedWorkerId, setSelectedWorkerId] = useState<string>('');
    const [selectedMachineRefs, setSelectedMachineRefs] = useState<string[]>([]);
    const [isBulkMode, setIsBulkMode] = useState(false);
    const [devFee, setDevFee] = useState<number | ''>('');
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchMachines();
    }, [page, rowsPerPage]);

    const fetchMachines = () => {
        setLoading(true);
        axios.get(`${API_PATH}/reseller-dashboard/myMachines?page=${page}&size=${rowsPerPage}`, getAxiosConfig())
            .then(res => {
                setMachines(res.data.content || []);
                setTotalElements(res.data.totalElements || 0);
            })
            .catch(err => {
                console.error(err);
                setSnackbar({ open: true, message: "Failed to load your machines", severity: 'error' });
            })
            .finally(() => setLoading(false));
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = machines
                .filter(m => m.hashVendor?.name === 'SEGMENT' && m.machineReference)
                .map((n) => n.machineReference) as string[];
            setSelectedMachineRefs(newSelected);
            return;
        }
        setSelectedMachineRefs([]);
    };

    const handleClick = (event: React.MouseEvent<unknown>, ref: string) => {
        const selectedIndex = selectedMachineRefs.indexOf(ref);
        let newSelected: string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedMachineRefs, ref);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedMachineRefs.slice(1));
        } else if (selectedIndex === selectedMachineRefs.length - 1) {
            newSelected = newSelected.concat(selectedMachineRefs.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedMachineRefs.slice(0, selectedIndex),
                selectedMachineRefs.slice(selectedIndex + 1),
            );
        }
        setSelectedMachineRefs(newSelected);
    };

    const isSelected = (ref: string) => ref ? selectedMachineRefs.indexOf(ref) !== -1 : false;

    const handleOpenModal = (workerId: string) => {
        setIsBulkMode(false);
        setSelectedWorkerId(workerId);
        setDevFee('');
        setModalOpen(true);
    };

    const handleOpenBulkModal = () => {
        setIsBulkMode(true);
        setDevFee('');
        setModalOpen(true);
    };

    const handleDisableAll = async () => {
        if (!window.confirm("Are you sure you want to disable ALL dev fees for your machines?")) return;
        setSaving(true);
        try {
            const res = await axios.post(API_PATH + '/reseller-dashboard/disableAllDevFees', {}, getAxiosConfig());
            if (res.data?.status === false) {
                setSnackbar({ open: true, message: res.data.message || "Failed to disable dev fees", severity: 'error' });
            } else {
                setSnackbar({ open: true, message: "All dev fees disabled successfully!", severity: 'success' });
                fetchMachines();
                setSelectedMachineRefs([]);
            }
        } catch (err: any) {
            setSnackbar({ open: true, message: err.response?.data?.message || err.message || "Error disabling dev fees", severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedWorkerId('');
        setIsBulkMode(false);
        setDevFee('');
    };

    const handleSaveDevFee = async () => {
        if (devFee === '' || Number(devFee) < 0 || Number(devFee) > 50) {
            setSnackbar({ open: true, message: "Please provide a valid dev fee between 0 and 50%", severity: 'warning' });
            return;
        }

        if (!isBulkMode && !selectedWorkerId) {
            setSnackbar({ open: true, message: "No worker selected", severity: 'warning' });
            return;
        }

        setSaving(true);
        try {
            if (isBulkMode) {
                const payload = {
                    machineReferences: selectedMachineRefs,
                    devFee: Number(devFee)
                };
                const res = await axios.post(API_PATH + '/reseller-dashboard/setupDevFee/bulk', payload, getAxiosConfig());
                if (res.data?.status === false) {
                    setSnackbar({ open: true, message: res.data.message || "Failed to setup dev fees", severity: 'error' });
                } else {
                    setSnackbar({ open: true, message: "Bulk dev fee applied successfully!", severity: 'success' });
                    handleCloseModal();
                    fetchMachines();
                    setSelectedMachineRefs([]);
                }
            } else {
                const payload = {
                    workerId: selectedWorkerId,
                    devFee: Number(devFee)
                };
                const res = await axios.post(API_PATH + '/reseller-dashboard/setupDevFee', payload, getAxiosConfig());
                if (res.data?.status === false) {
                    setSnackbar({ open: true, message: res.data.message || "Failed to setup dev fee", severity: 'error' });
                } else {
                    setSnackbar({ open: true, message: "Dev fee installed successfully!", severity: 'success' });
                    handleCloseModal();
                    fetchMachines();
                }
            }
        } catch (err: any) {
            console.error('Failed to setup dev fee', err);
            setSnackbar({ open: true, message: err.response?.data?.message || err.message || "Error installing dev fee", severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight="bold">
                    My Machines
                </Typography>
                <Box display="flex" gap={2}>
                    <Button variant="contained" color="secondary" onClick={handleDisableAll} disabled={saving}>
                        Disable All Dev Fees
                    </Button>
                    <Button variant="contained" color="primary" disabled={selectedMachineRefs.length === 0} onClick={handleOpenBulkModal}>
                        Set Dev Fee (Selected)
                    </Button>
                </Box>
            </Box>

            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selectedMachineRefs.length > 0 && selectedMachineRefs.length < machines.filter(m => m.machineReference && m.hashVendor?.name === 'SEGMENT').length}
                                        checked={machines.length > 0 && machines.filter(m => m.machineReference && m.hashVendor?.name === 'SEGMENT').length > 0 && selectedMachineRefs.length === machines.filter(m => m.machineReference && m.hashVendor?.name === 'SEGMENT').length}
                                        onChange={handleSelectAllClick}
                                    />
                                </TableCell>
                                <TableCell><strong>Worker ID</strong></TableCell>
                                <TableCell><strong>Model</strong></TableCell>
                                <TableCell><strong>Target Pool</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Dev Fee</strong></TableCell>
                                <TableCell><strong>Dev Fee (%)</strong></TableCell>
                                <TableCell><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {machines.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">No machines assigned to you.</TableCell>
                                </TableRow>
                            ) : machines.map(m => {
                                const isSegment = m.hashVendor?.name === 'SEGMENT';
                                const isSelectable = !!m.machineReference && isSegment;
                                const isItemSelected = isSelectable ? isSelected(m.machineReference!) : false;
                                return (
                                <TableRow key={m.id} hover onClick={(event) => isSelectable ? handleClick(event, m.machineReference!) : undefined} role="checkbox" tabIndex={-1} selected={isItemSelected}>
                                    <TableCell padding="checkbox">
                                        {isSelectable ? (
                                            <Checkbox checked={isItemSelected} />
                                        ) : null}
                                    </TableCell>
                                    <TableCell>{m.workerId || '-'}</TableCell>
                                    <TableCell>{m.machineModel?.modelName || '-'}</TableCell>
                                    <TableCell>{m.targetPool || '-'}</TableCell>
                                    <TableCell>{m.machineStatus || '-'}</TableCell>
                                    <TableCell>{(m.devFee !== undefined && m.devFee > 0) ? 'ON' : 'OFF'}</TableCell>
                                    <TableCell>{m.devFee !== undefined ? m.devFee + '%' : '0%'}</TableCell>
                                    <TableCell>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="primary"
                                            disabled={!m.workerId || m.hashVendor?.name !== 'SEGMENT'}
                                            onClick={(e) => { e.stopPropagation(); handleOpenModal(m.workerId!); }}
                                        >
                                            Set Dev Fee
                                        </Button>
                                    </TableCell>
                                </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {!loading && (
                <TablePagination
                    rowsPerPageOptions={[100, 200, 500,1000]}
                    component="div"
                    count={totalElements}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            )}

            <Modal open={modalOpen} onClose={handleCloseModal}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 4,
                }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Setup Dev Fee {isBulkMode && "(Bulk)"}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        {isBulkMode ? `${selectedMachineRefs.length} machines selected` : `Worker: ${selectedWorkerId}`}
                    </Typography>

                    <TextField
                        fullWidth
                        label="Dev Fee (%)"
                        type="number"
                        margin="normal"
                        inputProps={{ min: 0, max: 50, step: 0.1 }}
                        value={devFee}
                        onChange={(e) => setDevFee(e.target.value ? Number(e.target.value) : '')}
                        helperText="Enter a percentage between 0 and 50"
                    />

                    <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                        <Button variant="outlined" onClick={handleCloseModal} disabled={saving}>Cancel</Button>
                        <Button variant="contained" onClick={handleSaveDevFee} disabled={saving}>
                            {saving ? <CircularProgress size={20} /> : 'Save'}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity as any} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
